import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useStudentAuth } from '@/context/StudentAuthContext';
import { useAssetContext } from '@/context/AssetContext';
import { Mail, Lock, User, Eye, EyeOff, ArrowRight, GraduationCap, Globe, Users, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API_URL = window.location.origin;

const StudentLoginPage = ({ defaultMode }) => {
  const { isDark } = useTheme();
  const { login, register, loginWithGoogle, error } = useStudentAuth();
  const { getLogo } = useAssetContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get motivation from URL query param (from Journey Selector)
  const motivation = searchParams.get('motivation');
  
  const [isLogin, setIsLogin] = useState(defaultMode !== 'register');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  
  // Store motivation in sessionStorage for Google OAuth flow
  useEffect(() => {
    if (motivation) {
      sessionStorage.setItem('registration_motivation', motivation);
    }
    // Mark user as having visited the portal (for header button logic)
    localStorage.setItem('portal_visited', 'true');
  }, [motivation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        navigate('/uac-desk/dashboard');
      } else {
        // Pass motivation to register
        const storedMotivation = motivation || sessionStorage.getItem('registration_motivation');
        await register(formData.name, formData.email, formData.password, storedMotivation);
        sessionStorage.removeItem('registration_motivation');
        navigate('/uac-desk/dashboard');
      }
    } catch (err) {
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { icon: Users, value: '30,000+', label: 'Students Placed' },
    { icon: Globe, value: '300+', label: 'Universities' },
    { icon: Award, value: '98%', label: 'Visa Success' }
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        {/* Background with gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-violet-500/5 rounded-full blur-2xl" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <div>
            <Link to="/" className="inline-block">
              <img 
                src={getLogo()} 
                alt="UniXperts" 
                className="h-12 w-auto object-contain brightness-0 invert"
              />
            </Link>
          </div>
          
          {/* Main content */}
          <div className="space-y-8 max-w-lg">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/10">
              <GraduationCap className="w-4 h-4 text-cyan-400" />
              <span className="text-white/80 text-sm font-medium">Student Portal</span>
            </div>
            
            <h1 className="text-5xl font-bold text-white leading-tight">
              Your Gateway to
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-violet-400 bg-clip-text text-transparent">
                Global Education
              </span>
            </h1>
            
            <p className="text-white/60 text-lg leading-relaxed">
              Access AI-powered university matching, track your applications in real-time, and get personalized guidance from expert counselors.
            </p>
            
            {/* Features list */}
            <div className="space-y-3">
              {[
                'AI-powered university recommendations',
                'Document management & verification',
                'Real-time application tracking',
                'Expert counselor support'
              ].map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-white/70 text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Stats */}
          <div className="flex items-center gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <stat.icon className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-white font-bold">{stat.value}</p>
                  <p className="text-white/50 text-xs">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className={`flex-1 flex items-center justify-center p-6 lg:p-12 ${isDark ? 'bg-slate-900' : 'bg-gradient-to-br from-slate-50 to-white'}`}>
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 flex justify-center">
            <Link to="/">
              <img 
                src={getLogo()} 
                alt="UniXperts" 
                className={`h-10 w-auto object-contain ${isDark ? 'brightness-0 invert' : ''}`}
              />
            </Link>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {isLogin ? 'Welcome back!' : 'Create your account'}
            </h2>
            <p className={`mt-2 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
              {isLogin ? 'Sign in to continue your journey' : 'Start your study abroad journey today'}
            </p>
          </div>

          {/* Google Login */}
          <Button
            type="button"
            variant="outline"
            className={`w-full h-12 rounded-xl font-medium transition-all ${
              isDark 
                ? 'bg-white/5 border-white/20 text-white hover:bg-white/10' 
                : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50 shadow-sm'
            }`}
            onClick={loginWithGoogle}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className={`w-full border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`} />
            </div>
            <div className="relative flex justify-center">
              <span className={`px-4 text-sm ${isDark ? 'bg-slate-900 text-white/40' : 'bg-white text-slate-400'}`}>
                or continue with email
              </span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className={`text-sm font-medium ${isDark ? 'text-white/70' : 'text-slate-700'}`}>
                  Full Name
                </Label>
                <div className="relative mt-2">
                  <User className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-white/30' : 'text-slate-400'}`} />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`pl-12 h-12 rounded-xl ${
                      isDark 
                        ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-cyan-500' 
                        : 'bg-slate-50 border-slate-200 focus:border-blue-500 focus:bg-white'
                    }`}
                    required={!isLogin}
                    data-testid="register-name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="email" className={`text-sm font-medium ${isDark ? 'text-white/70' : 'text-slate-700'}`}>
                Email Address
              </Label>
              <div className="relative mt-2">
                <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-white/30' : 'text-slate-400'}`} />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`pl-12 h-12 rounded-xl ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-cyan-500' 
                      : 'bg-slate-50 border-slate-200 focus:border-blue-500 focus:bg-white'
                  }`}
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className={`text-sm font-medium ${isDark ? 'text-white/70' : 'text-slate-700'}`}>
                Password
              </Label>
              <div className="relative mt-2">
                <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-white/30' : 'text-slate-400'}`} />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={`pl-12 pr-12 h-12 rounded-xl ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-cyan-500' 
                      : 'bg-slate-50 border-slate-200 focus:border-blue-500 focus:bg-white'
                  }`}
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-4 top-1/2 -translate-y-1/2 ${isDark ? 'text-white/40 hover:text-white' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-medium shadow-lg shadow-blue-500/25 transition-all"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Please wait...
                </div>
              ) : (
                <>
                  {isLogin ? 'Sign In' : 'Create Account'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Toggle mode */}
          <p className={`mt-6 text-center text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
            {isLogin ? "Don't have an account?" : "Already have an account?"}
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="ml-2 text-blue-600 hover:text-blue-700 font-medium hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? 'Sign Up' : 'Sign In'}
            </button>
          </p>

          {/* Terms */}
          <p className={`mt-6 text-center text-xs ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
            By continuing, you agree to our{' '}
            <Link to="/terms" className="underline hover:text-blue-600">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="underline hover:text-blue-600">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default StudentLoginPage;
